<template>
  <div
    class="flex flex-col gap-4 border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6"
  >
    <div class="flex flex-wrap items-center gap-2 text-sm text-gray-500">
      <span>
        {{ startIndex + 1 }} - {{ Math.min(endIndex, totalItems) }} of
        {{ totalItems }} • Display
      </span>
      <select
        :value="pageSize"
        @change="$emit('page-size-change', parseInt($event.target.value))"
        class="ml-1 border border-gray-300 rounded-md px-2 py-1 text-sm bg-white focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none"
      >
        <option v-for="size in pageSizeOptions" :key="size" :value="size">
          {{ size }}
        </option>
      </select>
      <span>rows</span>
    </div>

    <div class="flex items-center justify-end gap-2 sm:justify-start">
      <button
        @click="$emit('prev-page')"
        :disabled="currentPage === 1"
        class="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-primary rounded-md"
      >
        <ChevronLeftIcon class="h-4 w-4" />
      </button>

      <div class="flex gap-1">
        <template v-for="page in visiblePages" :key="page">
          <button
            v-if="typeof page === 'number'"
            @click="$emit('page-change', page)"
            :class="[
              'min-w-[32px] h-8 px-3 text-sm font-medium rounded-md flex items-center justify-center focus:outline-none focus:ring-1 focus:ring-primary',
              page === currentPage
                ? 'bg-primary-neutral text-white'
                : 'text-gray-700 hover:bg-gray-100 bg-white border border-gray-300',
            ]"
          >
            {{ page }}
          </button>
          <span v-else class="px-2 py-2 text-gray-400 text-sm">...</span>
        </template>
      </div>

      <button
        @click="$emit('next-page')"
        :disabled="currentPage === totalPages"
        class="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-primary rounded-md"
      >
        <ChevronRightIcon class="h-4 w-4" />
      </button>
    </div>
  </div>
</template>

<script>
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/20/solid";

export default {
  name: "TablePagination",
  components: { ChevronLeftIcon, ChevronRightIcon },
  props: {
    currentPage: { type: Number, required: true },
    totalPages: { type: Number, required: true },
    totalItems: { type: Number, required: true },
    pageSize: { type: Number, required: true },
    pageSizeOptions: { type: Array, default: () => [6, 10, 20, 50] },
  },
  emits: ["page-change", "page-size-change", "prev-page", "next-page"],
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return Math.min(this.startIndex + this.pageSize, this.totalItems);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(total - 1, current + delta);
        i++
      ) {
        range.push(i);
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, "...");
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (current + delta < total - 1) {
        rangeWithDots.push("...", total);
      } else {
        if (total > 1) rangeWithDots.push(total);
      }

      return rangeWithDots.filter(
        (item, index, arr) => arr.indexOf(item) === index
      );
    },
  },
};
</script>
